import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ContentViewer.css';

/**
 * Determine display type from file extension.
 */
const getDisplayType = (filePath) => {
  if (!filePath) return 'other';
  const ext = filePath.split('.').pop().toLowerCase();
  if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'flac', 'aac', 'm4a'].includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp', 'webp'].includes(ext)) return 'image';
  if (['pptx', 'ppt'].includes(ext)) return 'pptx';
  if (['docx'].includes(ext)) return 'docx';
  if (['txt', 'csv', 'md', 'py', 'js', 'java', 'c', 'cpp', 'h', 'html', 'css', 'json', 'xml'].includes(ext)) return 'text';
  return 'other';
};

const getContentIcon = (filePath) => {
  const t = getDisplayType(filePath);
  switch (t) {
    case 'video': return '🎥';
    case 'audio': return '🔊';
    case 'pdf': return '📄';
    case 'image': return '🖼️';
    case 'pptx': return '📊';
    case 'docx': return '📝';
    case 'text': return '📃';
    default: return '📁';
  }
};

const ContentViewer = ({ courseId, weekNo, studentId, onContentViewed }) => {
  const [contents, setContents] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewedContents, setViewedContents] = useState(new Set());

  // Preview data for DOCX/PPTX/TXT
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // PPTX slide pagination
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    loadWeekContent();
  }, [weekNo]);

  const loadWeekContent = async () => {
    try {
      setLoading(true);
      const [contentRes, progressRes] = await Promise.all([
        axios.get(`/api/contents/by-course/${courseId}?week=${weekNo}`),
        axios.get(`/api/progress/${studentId}/${courseId}`)
      ]);

      const weekContents = (contentRes.data || []).filter(c => c.week_no === weekNo);
      setContents(weekContents);

      const accessedIds = progressRes.data?.accessed_content_ids || [];
      setViewedContents(new Set(accessedIds));

      // Auto-select first content but do NOT mark it as read
      if (weekContents.length > 0) {
        setSelectedContent(weekContents[0]);
        loadPreviewIfNeeded(weekContents[0]);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Mark content as viewed (called explicitly on user action) ──
  const markAsViewed = useCallback(async (contentId) => {
    if (viewedContents.has(contentId)) return;
    try {
      await axios.post('/api/progress/mark-accessed', {
        student_id: studentId,
        course_id: courseId,
        content_id: contentId
      });
      setViewedContents(prev => new Set([...prev, contentId]));
      onContentViewed?.(contentId);
    } catch (error) {
      console.error('Failed to mark content as viewed:', error);
    }
  }, [viewedContents, studentId, courseId, onContentViewed]);

  // Auto-mark currently displayed non-media content as viewed.
  // This handles single-item weeks where the first item is auto-selected
  // and the learner may not click the sidebar item explicitly.
  useEffect(() => {
    if (!selectedContent?.content_id) return;
    const dtype = getDisplayType(selectedContent.file_path);
    if (dtype !== 'video' && dtype !== 'audio') {
      markAsViewed(selectedContent.content_id);
    }
  }, [selectedContent, markAsViewed]);

  // ── Load preview for DOCX / PPTX / TXT via backend conversion ──
  const loadPreviewIfNeeded = async (content) => {
    const dtype = getDisplayType(content.file_path);
    if (['docx', 'pptx', 'text'].includes(dtype)) {
      setPreviewLoading(true);
      setPreviewData(null);
      setCurrentSlide(0);
      try {
        const res = await axios.get(`/api/content-preview/${content.content_id}?student_id=${studentId}`);
        setPreviewData(res.data);
      } catch (err) {
        console.error('Preview load failed:', err);
        setPreviewData({ type: 'download', url: getContentDownloadUrl(content) });
      } finally {
        setPreviewLoading(false);
      }
    } else {
      setPreviewData(null);
      setPreviewLoading(false);
    }
  };

  // ── When user clicks a content item in sidebar → select AND mark as read ──
  const handleContentSelect = (content) => {
    setSelectedContent(content);
    setVideoError(false);
    setVideoPreparing(false);
    loadPreviewIfNeeded(content);

    const dtype = getDisplayType(content.file_path);
    // For video/audio, mark on play instead. For everything else, mark now.
    if (dtype !== 'video' && dtype !== 'audio') {
      markAsViewed(content.content_id);
    }
  };

  // ── Render the main content area ──
  const [videoError, setVideoError] = useState(false);
  const [videoPreparing, setVideoPreparing] = useState(false);

  const getVideoMimeType = (filePath) => {
    if (!filePath) return '';
    const ext = filePath.split('.').pop().toLowerCase();
    const mimeMap = {
      mp4: 'video/mp4', webm: 'video/webm', ogg: 'video/ogg',
      mov: 'video/quicktime', mkv: 'video/x-matroska', avi: 'video/x-msvideo'
    };
    return mimeMap[ext] || '';
  };

  // Formats that browsers can actually play natively
  const isBrowserPlayable = (filePath) => {
    if (!filePath) return false;
    const ext = filePath.split('.').pop().toLowerCase();
    // Only MP4 (H.264), WebM, and OGG are broadly supported in browsers
    return ['mp4', 'webm', 'ogg'].includes(ext);
  };

  const buildAccessQuery = () => {
    const params = new URLSearchParams();
    if (studentId) {
      params.set('student_id', studentId);
    }

    const query = params.toString();
    return query ? `?${query}` : '';
  };

  const getContentViewUrl = (content) => {
    if (!content?.content_id) return '';
    return `/api/content-files/${content.content_id}/view${buildAccessQuery()}`;
  };

  const getContentDownloadUrl = (content) => {
    if (!content?.content_id) return '';
    return `/api/content-files/${content.content_id}/download${buildAccessQuery()}`;
  };

  // Download handler — validate server response before saving file
  const handleDownload = async (content) => {
    const url = getContentDownloadUrl(content);
    if (!url) return;

    try {
      const resp = await fetch(url, { method: 'GET' });

      if (!resp.ok) {
        let message = 'File not found on server.';
        try {
          const errData = await resp.json();
          message = errData?.error || message;
        } catch (_) {
          // Keep default message when backend does not return JSON
        }
        window.alert(`Download failed: ${message}`);
        return;
      }

      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      const serverFilename = decodeURIComponent((filenameMatch?.[1] || filenameMatch?.[2] || '').trim());

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = serverFilename || content?.title || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Download failed:', error);
      window.alert('Download failed. Please try again.');
    }
  };

  // For non-browser-native video formats, use the stream/convert endpoint
  const getVideoSrc = (content) => {
    if (!content?.content_id) return '';

    if (!isBrowserPlayable(content.file_path)) {
      // Backend converts to MP4 (cached) and serves with Range support
      return `/api/content-files/${content.content_id}/stream${buildAccessQuery()}`;
    }

    return getContentViewUrl(content);
  };

  const renderContent = (content) => {
    const dtype = getDisplayType(content.file_path);
    const fileUrl = getContentViewUrl(content);
    const isViewed = viewedContents.has(content.content_id);

    switch (dtype) {
      case 'video': {
        const videoSrc = getVideoSrc(content);
        const needsTranscode = !isBrowserPlayable(content.file_path);
        return (
          <div className="content-player">
            {videoError ? (
              <div className="video-error-fallback">
                <div className="error-icon">🎥</div>
                <h4>Video cannot be played in browser</h4>
                <p>The video format may not be supported, or the file is still being prepared. Try again in a moment or download instead.</p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px' }}>
                  <button
                    className="btn-primary"
                    onClick={() => { setVideoError(false); setVideoPreparing(true); }}
                  >
                    🔄 Retry
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => handleDownload(content)}
                  >
                    ⬇ Download Video
                  </button>
                </div>
              </div>
            ) : (
              <>
                {needsTranscode && (
                  <div className="transcode-notice">
                    🔄 Preparing video for playback — this may take a moment for the first view
                  </div>
                )}
                <video
                  key={`${content.content_id}-${needsTranscode ? 'stream' : 'native'}-${videoPreparing}`}
                  controls
                  preload="auto"
                  onPlay={() => {
                    setVideoPreparing(false);
                    if (!isViewed) markAsViewed(content.content_id);
                  }}
                  onCanPlay={() => setVideoPreparing(false)}
                  onError={(e) => {
                    console.error('Video error:', e.target.error);
                    // Don't immediately show error for transcoded videos — retry once
                    if (needsTranscode && !videoError) {
                      console.log('Transcoded video error — retrying in 3s...');
                      setTimeout(() => {
                        setVideoError(false);
                        setVideoPreparing(true);
                      }, 3000);
                    }
                    setVideoError(true);
                  }}
                  data-cid={content.content_id}
                  style={{ width: '100%', maxHeight: '560px', background: '#000', borderRadius: 8 }}
                >
                  <source src={videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </>
            )}
          </div>
        );
      }

      case 'audio':
        return (
          <div className="content-player audio-player">
            <div className="audio-icon">🎵</div>
            <audio
              key={content.content_id}
              controls
              onPlay={() => !isViewed && markAsViewed(content.content_id)}
              style={{ width: '100%' }}
            >
              <source src={fileUrl} />
            </audio>
          </div>
        );

      case 'pdf':
        return (
          <div className="content-inline-viewer">
            <iframe
              key={content.content_id}
              title={content.title || 'PDF Document'}
              src={fileUrl}
              style={{ width: '100%', height: '650px', border: 'none', borderRadius: 8 }}
            />
          </div>
        );

      case 'image':
        return (
          <div className="content-image-viewer">
            <img src={fileUrl} alt={content.title} />
          </div>
        );

      case 'docx':
        return renderDocxPreview();

      case 'pptx':
        return renderPptxPreview(content);

      case 'text':
        return renderTextPreview();

      default:
        return (
          <div className="content-fallback">
            <div className="fallback-icon">📄</div>
            <h4>{content.title}</h4>
            <p>This file type cannot be previewed in the browser.</p>
            <button className="btn-primary" onClick={() => handleDownload(content)}>
              ⬇ Download File
            </button>
          </div>
        );
    }
  };

  // ── DOCX preview (HTML from mammoth) ──
  const renderDocxPreview = () => {
    if (previewLoading) return <div className="preview-loading"><div className="spinner" /> Loading document...</div>;
    if (!previewData || previewData.type === 'download') {
      return (
        <div className="content-fallback">
          <p>Unable to preview this document.</p>
          <a href={previewData?.url || '#'} className="btn-primary">⬇ Download to View</a>
        </div>
      );
    }
    return (
      <div className="content-inline-viewer docx-viewer">
        <div className="docx-content" dangerouslySetInnerHTML={{ __html: previewData.html }} />
      </div>
    );
  };

  // ── PPTX preview (slide-by-slide) ──
  const renderPptxPreview = (content) => {
    if (previewLoading) return <div className="preview-loading"><div className="spinner" /> Loading presentation...</div>;
    if (!previewData || previewData.type === 'download' || !previewData.slides) {
      const inlineUrl = getContentViewUrl(content);
      return (
        <div className="content-fallback">
          <p>Unable to preview this presentation.</p>
          {inlineUrl && (
            <iframe
              title={content?.title || 'File Viewer'}
              src={inlineUrl}
              style={{ width: '100%', height: '520px', border: 'none', borderRadius: 8, marginBottom: 12 }}
            />
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            {inlineUrl && (
              <a href={inlineUrl} target="_blank" rel="noreferrer" className="btn-primary">Open in New Tab</a>
            )}
            <button className="btn-primary" onClick={() => handleDownload(content)}>⬇ Download to View</button>
          </div>
        </div>
      );
    }

    const slides = previewData.slides;
    const slide = slides[currentSlide];

    return (
      <div className="content-inline-viewer pptx-viewer">
        <div className="slide-navigation">
          <button
            className="slide-nav-btn"
            onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
            disabled={currentSlide === 0}
          >
            ◀ Prev
          </button>
          <span className="slide-counter">
            Slide {currentSlide + 1} of {slides.length}
          </span>
          <button
            className="slide-nav-btn"
            onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
            disabled={currentSlide === slides.length - 1}
          >
            Next ▶
          </button>
        </div>

        <div className="slide-content">
          {slide.images && slide.images.length > 0 && (
            <div className="slide-images">
              {slide.images.map((img, i) => (
                <img key={i} src={img.data} alt={`Slide ${slide.slide_no} image ${i + 1}`} className="slide-image" />
              ))}
            </div>
          )}
          {slide.texts && slide.texts.length > 0 && (
            <div className="slide-texts">
              {slide.texts.map((text, i) => (
                <p key={i} className="slide-text-line">{text}</p>
              ))}
            </div>
          )}
          {(!slide.texts || slide.texts.length === 0) && (!slide.images || slide.images.length === 0) && (
            <p className="slide-empty">This slide has no text or image content.</p>
          )}
        </div>

        {/* Slide thumbnails */}
        <div className="slide-thumbnails">
          {slides.map((s, idx) => (
            <button
              key={idx}
              className={`slide-thumb ${idx === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(idx)}
              title={`Slide ${idx + 1}`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ── Text file preview ──
  const renderTextPreview = () => {
    if (previewLoading) return <div className="preview-loading"><div className="spinner" /> Loading file...</div>;
    if (!previewData || previewData.type === 'download') {
      return (
        <div className="content-fallback">
          <p>Unable to preview this file.</p>
          <a href={previewData?.url || '#'} className="btn-primary">⬇ Download to View</a>
        </div>
      );
    }
    return (
      <div className="content-inline-viewer text-viewer">
        <pre className="text-content">{previewData.text}</pre>
      </div>
    );
  };

  if (loading) return <div className="content-loader"><div className="spinner" /> Loading content...</div>;

  if (contents.length === 0) {
    return (
      <div className="no-content">
        <p>📭 No materials available for this week yet</p>
      </div>
    );
  }

  return (
    <div className="content-viewer-container">
      {/* Sidebar: content list */}
      <div className="content-sidebar">
        <h4>📚 Week {weekNo} Materials</h4>
        <div className="content-list">
          {contents.map(content => (
            <button
              key={content.content_id}
              className={`content-item ${selectedContent?.content_id === content.content_id ? 'active' : ''}`}
              onClick={() => handleContentSelect(content)}
            >
              <span className="content-icon">{getContentIcon(content.file_path)}</span>
              <span className="content-title">{content.title}</span>
              {viewedContents.has(content.content_id) && <span className="viewed-badge">✓</span>}
            </button>
          ))}
        </div>

        <div className="content-progress-summary">
          <span>{contents.filter(c => viewedContents.has(c.content_id)).length} / {contents.length} viewed</span>
        </div>
      </div>

      {/* Main: content display */}
      <div className="content-main">
        {selectedContent ? (
          <div className="content-display">
            <div className="content-header">
              <h3>{selectedContent.title}</h3>
              <div className="content-header-actions">
                {viewedContents.has(selectedContent.content_id) && (
                  <span className="viewed-status">✓ Viewed</span>
                )}
                <button
                  className="btn-download"
                  title="Download this file"
                  onClick={() => handleDownload(selectedContent)}
                >
                  ⬇ Download
                </button>
              </div>
            </div>

            <div className="content-body">
              {renderContent(selectedContent)}
            </div>
          </div>
        ) : (
          <div className="no-content">
            <p>Select a content item from the sidebar</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentViewer;
